import { FC, PropsWithChildren, useEffect, useState, useRef } from 'react';
import { Modal, Button, Group, Text, Divider } from '@mantine/core';
import { useMedplumNavigate } from '@medplum/react';
import { Patient, RelatedPerson } from '@medplum/fhirtypes';
import { useDisclosure } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';
import { IconPlus, IconCircleCheck } from '@tabler/icons-react';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import 'react-phone-input-2/lib/style.css';
import DynamicConfigService from '@configuration/dynamic-config.service';
import { useDynamicValidation } from '@hooks/useDynamicValidation';
import { CreatePatientField } from './CreatePatientField';
import {
  getFormValuesFromPatient,
  getFormValuesFromRelatedPerson,
  getInitialValues,
} from '@utils/create-patient-helpers';
import {
  ChoremeioFormConfig,
  FieldItem,
} from '@configuration/components/choremeioFields.config';
import { useHandlePatientSubmit } from '@hooks/useHandlePatientSubmit';
import { useHandleRelatedPersonSubmit } from '@hooks/useHandleRelatedPersonSubmit';
import { AmkaValidationModal } from './AmkaValidationModal';
import { HL7 } from '@/consts/systems.consts';
import FormActionButtons, { FormActionButtonsRef } from '@/components/FormActionButtons';
import { isEqual } from 'lodash';

// -----------------------------------------------------------------------------
interface ChoremeioCreatePatientProps extends PropsWithChildren {
  patient?: Patient | null;
  existingRelatedPeople?: RelatedPerson[];
  onUpdatePatient?: (updatedPatient: Patient) => void;
  onUpdateRelatedPerson?: (updatedPatient: Patient) => void;
}

// -----------------------------------------------------------------------------
type RelationType = 'father' | 'mother' | 'spouse';
const RELATION_TYPES: RelationType[] = ['father', 'mother', 'spouse'];

// -----------------------------------------------------------------------------
const ChoremeioCreatePatient: FC<ChoremeioCreatePatientProps> = ({
  patient = null,
  existingRelatedPeople = [],
  children,
  onUpdatePatient,
  onUpdateRelatedPerson,
}) => {
  const [
    createModalOpened,
    { open: openCreateModal, close: closeCreateModal },
  ] = useDisclosure(false);
  const [
    validateIdentifierModalOpened,
    { open: openValidateIdentifierModal, close: closeValidateIdentifierModal },
  ] = useDisclosure(false);
  const navigate = useMedplumNavigate();
  const { t } = useTranslation(['common', 'create-patient-modal']);
  const { handlePatientSubmit } = useHandlePatientSubmit();
  const { handleRelatedPersonSubmit } = useHandleRelatedPersonSubmit();

  // ---------------------------------------------------------------------------
  const configService = DynamicConfigService.getInstance();

  const fieldsConfig = configService.getConfig(
    'choremeioFields'
  ) as ChoremeioFormConfig;

  const requiredFields = fieldsConfig.choremeioFieldsConfig;
  const validators = useDynamicValidation(requiredFields);
  const initialValues = getInitialValues(requiredFields);
  const form = useForm({
    initialValues,
    validate: validators,
  });
  const baselineRef = useRef<typeof form.values | null>(null);
  const formActionsRef = useRef<FormActionButtonsRef>(null);
  //----------------------------------------------------------------------------
  const [relatedPersons, setRelatedPersons] = useState<
    Partial<Record<RelationType, RelatedPerson>>
  >({});

  const [isPrefilledWithAmka, setIsPrefilledWithAmka] = useState(false);

  // ---------------------------------------------------------------------------
  useEffect(() => {
    const fetchRelatedPersons = async () => {
      if (!createModalOpened || !patient) {
        // Only reset if we haven't pre-filled with AMKA
        if (!isPrefilledWithAmka) {
          form.reset();
        }
        // If dialog just opened for a new patient, capture a clean baseline
        if (createModalOpened && !patient) {
          baselineRef.current = { ...(form.getValues() as typeof form.values) };
        }
        return;
      }

      form.setValues(getFormValuesFromPatient(patient));

      const results = RELATION_TYPES.map((type) => {
        const relativePatient = existingRelatedPeople.find(
          (rp) =>
            rp.relationship?.[0]?.coding?.[0]?.code ===
            (type === 'father'
              ? HL7.TERMINOLOGY.CS.V3.ROLE_CODE.CODES.FATHER
              : type === 'mother'
                ? HL7.TERMINOLOGY.CS.V3.ROLE_CODE.CODES.MOTHER
                : HL7.TERMINOLOGY.CS.V3.ROLE_CODE.CODES.SPOUSE)
        ) as RelatedPerson | undefined;
        return { type, relativePatient };
      });

      const newRelatedPersons: Partial<Record<RelationType, RelatedPerson>> =
        {};
      const mergedFormValues = results.reduce(
        (acc, { type, relativePatient }) => {
          if (relativePatient) {
            newRelatedPersons[type] = relativePatient;
            return {
              ...acc,
              ...getFormValuesFromRelatedPerson(relativePatient, type),
            };
          }
          return acc;
        },
        {}
      );

      form.setValues((current) => ({
        ...current,
        ...mergedFormValues,
      }));
      setRelatedPersons(newRelatedPersons);

      // capture baseline after values are populated
      baselineRef.current = { ...(form.getValues() as typeof form.values) };
    };

    fetchRelatedPersons();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createModalOpened, patient]);

  // ---------------------------------------------------------------------------
  const handleAmkaValidated = (amka: string) => {
    // Pre-fill the AMKA field in the main form with the validated AMKA
    form.setValues(() => ({
      ...getInitialValues(requiredFields),
      amka: amka,
    }));
    setIsPrefilledWithAmka(true);
    openCreateModal();
  };

  // ---------------------------------------------------------------------------
  const handleRedirectToPatient = (patient: Patient) => {
    navigate(`/Patient/${patient.id}`);
  };

  // ---------------------------------------------------------------------------
  // Reset prefill flag when modal closes
  const handleCloseCreateModal = () => {
    setIsPrefilledWithAmka(false);
    closeCreateModal();
  };

  //----------------------------------------------------------------------------
  const hasUnsavedChanges = (): boolean => {
    if (!baselineRef.current) {
      return false;
    }
    try {
      return !isEqual(form.getValues(), baselineRef.current);
    } catch {
      return true;
    }
  };

  const handleDiscardChanges = () => {
    if (baselineRef.current) {
      form.setValues(baselineRef.current);
    }
  };

  // ---------------------------------------------------------------------------
  const onSubmit = async (values: typeof form.values): Promise<void> => {
    try {
      //------------------------------------------------------------------------
      const submittedPatient = await handlePatientSubmit(values, patient);

      // Update parent component to trigger immediate re-render
      onUpdatePatient?.(submittedPatient);
      //------------------------------------------------------------------------
      await Promise.all(
        RELATION_TYPES.map(async (type) =>
          handleRelatedPersonSubmit(
            relatedPersons[type] ?? null,
            values,
            type,
            submittedPatient
          )
        )
      );

      // Trigger refresh for related persons
      onUpdateRelatedPerson?.(submittedPatient);

      //------------------------------------------------------------------------
      if (!patient && submittedPatient) {
        navigate(`/Patient/${submittedPatient.id}`);
      }
    } catch (error) {
      console.error('Error creating or updating patient:', error);
    } finally {
      notifications.show({
        color: 'green',
        icon: <IconCircleCheck />,
        title: 'Success',
        message: `Patient ${patient ? 'updated' : 'created'}`,
      });

      handleCloseCreateModal();
    }
  };

  // ---------------------------------------------------------------------------
  return (
    <>
      {' '}
      <Modal
        opened={createModalOpened}
        onClose={() => formActionsRef.current?.handleGuardedClose()}
        size={700}
        title={
          <>
            <Text fw={700} size="lg">
              {patient
                ? t('labels.editTitle', { ns: 'create-patient-modal' })
                : t('labels.createTitle', { ns: 'create-patient-modal' })}
            </Text>
            <Text size="sm" color="dimmed" mb="sm">
              {patient
                ? t('descriptions.editDesc', { ns: 'create-patient-modal' })
                : t('descriptions.createDesc', { ns: 'create-patient-modal' })}
            </Text>
          </>
        }
        centered
        closeOnClickOutside={false}
        closeOnEscape={false}
      >
        <form
          onSubmit={form.onSubmit(onSubmit)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.preventDefault();
          }}
        >
          {requiredFields.map((fieldConfig, index) => {
            if (fieldConfig.type === 'group') {
              return (
                <div key={index}>
                  <Group grow preventGrowOverflow={true}>
                    {fieldConfig.fields?.map((field, idx) => (
                      <CreatePatientField
                        key={`${index}-${idx}`}
                        field={field as FieldItem}
                        placeholderAppear={true}
                        form={form}
                      />
                    ))}
                  </Group>
                </div>
              );
            }
            if (fieldConfig.type === 'divider') {
              return (
                <Divider
                  my="xs"
                  label={t(`labels.${fieldConfig.label}`, 'Label', {
                    ns: 'create-patient-modal',
                  })}
                  labelPosition="left"
                />
              );
            }

            if (fieldConfig.type === 'field') {
              return (
                <div key={index}>
                  <CreatePatientField
                    key={`${index}`}
                    field={fieldConfig as FieldItem}
                    placeholderAppear={true}
                    form={form}
                  />
                </div>
              );
            }
            return null;
          })}{' '}
          <FormActionButtons
            ref={formActionsRef}
            onDiscard={handleDiscardChanges}
            onClose={handleCloseCreateModal}
            hasUnsavedChanges={hasUnsavedChanges()}
            saveLabel={t('labels.saveButton', { ns: 'create-patient-modal' })}
          />{' '}
        </form>
      </Modal>
      <AmkaValidationModal
        opened={validateIdentifierModalOpened}
        onClose={closeValidateIdentifierModal}
        onValidated={handleAmkaValidated}
        onRedirectToPatient={handleRedirectToPatient}
      />
      {children ? (
        <div onClick={openCreateModal}>{children}</div>
      ) : (
        <Button
          leftSection={<IconPlus size={16} />}
          onClick={openValidateIdentifierModal}
        >
          {t('triggerButtonText', { ns: 'create-patient-modal' })}
        </Button>
      )}
    </>
  );
};

export default ChoremeioCreatePatient;
